import { FC, useState, useEffect } from 'react';
import MemberListPage from './pages/MemberListPage';
import SettingsAndReportsPage from './pages/SettingsAndReportsPage';
import InsightsDashboard from './pages/InsightsDashboard';
import GlassMemberListPage from './pages/GlassMemberListPage';
import ActionsPanel from './components/actions/ActionsPanel';
import TestStickyHeader from './pages/TestStickyHeader';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';
import Login from './pages/Login/Login';
import Logout from './pages/Logout/Logout';
import PrivacyKpiDashboard from './pages/PrivacyKpiDashboard';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { ADMIN_ROLES, FEATURE_PRIVACY_KPI_ENABLED } from './hooks/useHasRole';
import { DataSourceProvider } from './contexts/DataSourceContext';
import { PinnedActionsProvider } from './contexts/PinnedActionsContext';
import { CompletedActionsProvider } from './contexts/CompletedActionsContext';
import { AppConfigProvider, useAppConfig } from './context/AppConfigContext';
import { UserProvider } from './context/UserContext';
import { GlassHeader } from './components/ui/glass-header';
import { DebugContextProvider } from './context/DebugContextProvider';
import { FloatingVeraBadge } from './components/ui/FloatingVeraBadge';
import { MobileChartProvider } from './context/MobileChartContext';
import { EvidenceProvider } from './context/EvidenceContext';
import ErrorBoundary from './components/ui/ErrorBoundary';
import useAppState from './common/AppState';
import AutoLogout from './common/AutoLogout';
import UserActivityMonitor from './common/UserActivityMonitor';

/**
 * AppContent component for the main content of the app
 * This uses the AppConfig context and handles authentication
 */
const AppContent: FC = () => {
  // State for filter panel
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Get the themeMode from context
  const { themeMode } = useAppConfig();

  // Get auth state from Zustand store
  const { isAuthenticated, fetchProfileData, isLoggedOut } = useAppState();

  // Comprehensive logging for debugging
  console.log('🔄 [AppContent] RENDER', {
    isAuthenticated,
    isLoggedOut,
    timestamp: new Date().toISOString()
  });

  // Get the view from URL params
  const getViewFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') || '';
  };

  // State for current view
  const [view, setView] = useState(getViewFromURL());

  // Check authentication on mount
  useEffect(() => {
    // Don't check auth if we're on login or logout pages
    if (view === 'login' || view === 'logout') {
      return;
    }

    // Force session verification on app load (forceCheck=true)
    // This ensures we detect expired sessions even if sessionStorage says we're authenticated
    console.log('🔍 [App] Forcing session verification on mount');
    fetchProfileData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, not when fetchProfileData changes

  // Initialize AutoLogout for inactivity timeout and UserActivityMonitor for session heartbeat
  useEffect(() => {
    // Skip in demo mode
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      console.log('🔧 [App] DEMO MODE: Skipping AutoLogout and UserActivityMonitor');
      return;
    }

    // Only initialize if authenticated
    if (isAuthenticated !== 'yes') {
      return;
    }

    // Get timeout from env var (default 30 minutes)
    const timeoutMinutes = parseInt(import.meta.env.VITE_AUTO_LOGOUT_MINUTES || '30', 10);

    // Logout handler
    const handleAutoLogout = () => {
      console.warn('⏱️ [App] Auto-logout triggered due to inactivity');
      // Navigate to logout page
      window.history.replaceState({}, '', '?view=logout&reason=timeout');
      window.dispatchEvent(new PopStateEvent('popstate'));
    };

    // Initialize AutoLogout
    const autoLogout = new AutoLogout(handleAutoLogout, timeoutMinutes);

    // Initialize UserActivityMonitor (5 minutes = 300 seconds)
    const userActivityMonitor = new UserActivityMonitor(300);

    // Cleanup on unmount or when auth changes
    return () => {
      autoLogout.destroy();
      userActivityMonitor.destroy();
    };
  }, [isAuthenticated]);

  // Redirect to login if not authenticated (and not already on login/logout)
  // DEMO MODE: Skip auth redirect when VITE_DEMO_MODE=true
  useEffect(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      return; // Skip auth check in demo mode
    }
    // Don't redirect if we're in the logout flow - let Logout component handle it
    if (isAuthenticated === 'no' && view !== 'login' && view !== 'logout' && !isLoggedOut) {
      window.history.pushState({}, '', '?view=login');
      setView('login');
    }
  }, [isAuthenticated, view, isLoggedOut]);

  // Update view when URL changes
  useEffect(() => {
    const handleLocationChange = () => {
      setView(getViewFromURL());
    };

    // Listen for both popstate (browser back/forward) and our custom navigation event
    window.addEventListener('popstate', handleLocationChange);

    // Force an initial check to make sure view is synced with URL
    handleLocationChange();

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  // Toggle filter panel function
  const toggleFilterPanel = () => {
    setIsFilterPanelOpen(!isFilterPanelOpen);
  };

  // Function to render the appropriate page based on view
  const renderPage = () => {
    console.log('🎯 [App] renderPage called, view:', view, 'isAuthenticated:', isAuthenticated);

    // Auth pages - no header needed
    if (view === 'login') {
      return <Login />;
    }
    if (view === 'logout') {
      return <Logout />;
    }

    // Show loading while checking auth
    if (isAuthenticated === 'maybe') {
      console.log('⏳ [App] Still checking auth, showing loading...');
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    console.log('✅ [App] Rendering protected page for view:', view, 'at', new Date().toISOString());

    // Protected pages - require authentication
    // Use stable keys to prevent unnecessary remounts
    switch (view) {
      case 'settings':
        return <SettingsAndReportsPage key="settings-page" />;
      case 'insights':
        return <InsightsDashboard key="insights-page" />;
      case 'actions':
        return <ActionsPanel key="actions-page" />;
      case 'profile':
        return <ProfilePage key="profile-page" />;
      case 'notifications':
        return <NotificationsPage key="notifications-page" />;
      case 'glass-members':
        return (
          <GlassMemberListPage
            key="glass-members-page"
            isFilterPanelOpen={isFilterPanelOpen}
            onFilterToggle={toggleFilterPanel}
            updateActiveFilters={() => {}}
          />
        );
      case 'test-sticky':
        return <TestStickyHeader key="test-sticky-page" />;
      case 'admin/privacy-kpis':
        // PRIV-25.2: Protected route for admin/auditor only
        // Feature flag check - if disabled, fall through to default (member list)
        if (!FEATURE_PRIVACY_KPI_ENABLED) {
          console.log('[App] Privacy KPI feature flag disabled, redirecting to default');
          return (
            <MemberListPage
              key="member-list-page"
              isFilterPanelOpen={isFilterPanelOpen}
              onFilterToggle={toggleFilterPanel}
              updateActiveFilters={() => {}}
            />
          );
        }
        return (
          <ProtectedRoute roles={[...ADMIN_ROLES]} key="privacy-kpis-route">
            <PrivacyKpiDashboard key="privacy-kpis-page" />
          </ProtectedRoute>
        );
      default:
        console.log('📄 [App] Rendering MemberListPage (default view)');
        return (
          <MemberListPage
            key="member-list-page"
            isFilterPanelOpen={isFilterPanelOpen}
            onFilterToggle={toggleFilterPanel}
            updateActiveFilters={() => {}}
          />
        );
    }
  };

  // Always use the gradient variant for all views, including Member Details
  const useGradientHeader = true;
  
  // Hide filter button in global nav header for all views
  const hideFilter = true;

  // Set the page title to always be "VeraScore Lenders Dashboard"
  useEffect(() => {
    document.title = 'VeraScore Lenders Dashboard';
  }, []);

  // For active filters status
  const hasActiveFilters = false;

  // Check if we're on an auth page (no header/badge needed)
  const isAuthPage = view === 'login' || view === 'logout';

  // Auth pages render without the standard layout
  if (isAuthPage) {
    return (
      <div className="min-h-screen bg-gray-100 relative">
        {renderPage()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 relative">
      <GlassHeader
        variant={useGradientHeader ? 'gradient' : 'default'}
        sticky
        isFilterOpen={isFilterPanelOpen}
        onFilterToggle={toggleFilterPanel}
        hideFilter={hideFilter}
        hasActiveFilters={hasActiveFilters}
        className={themeMode === 'light' ? 'light-mode' : ''}
      />
      <div className="relative z-0 content-wrapper bg-gray-100">
        {renderPage()}
        <FloatingVeraBadge />
      </div>
    </div>
  );
};

/**
 * Main App component that wraps all providers
 */
const App: FC = () => {
  return (
    <ErrorBoundary fallbackRoute="?view=login">
      <DataSourceProvider>
        <AppConfigProvider>
          <DebugContextProvider>
            <UserProvider>
              <PinnedActionsProvider>
                <CompletedActionsProvider>
                  <MobileChartProvider>
                    <EvidenceProvider>
                      <AppContent />
                    </EvidenceProvider>
                  </MobileChartProvider>
                </CompletedActionsProvider>
              </PinnedActionsProvider>
            </UserProvider>
          </DebugContextProvider>
        </AppConfigProvider>
      </DataSourceProvider>
    </ErrorBoundary>
  );
};

export default App;
